/**
 * useActivationCelebrations — fires a one-time toast when a step
 * transitions incomplete → complete in this session. Critical
 * detail: on first ever load for this user we seed every currently
 * completed step into the "already celebrated" set so existing
 * studios don\u2019t get carpet-bombed with nine toasts.
 */
import { useEffect, useRef } from 'react';
import { useToast } from '@/hooks/use-toast';
import type { ActivationStep, ActivationStepId } from '@/hooks/useActivationProgress';

const SEEN_KEY = 'orbit:activation:celebrated';
const SEED_FLAG = 'orbit:activation:seeded';

const COPY: Record<ActivationStepId, { title: string; description: string }> = {
  profile:       { title: 'Business profile set.',     description: 'Orbit knows who you are now.' },
  // ONB-1 — softened from "It's a real business now." which read
  // corporate. Fresh studios reading this on TestFlight shouldn't
  // feel judged for the state of their business before they added a
  // brand color; they should feel like something clicked into place.
  brand:         { title: 'Your color is on.',          description: "Every public surface carries it now — forms, portals, delivery pages, the works." },
  intake_form:   { title: 'Intake form is live.',      description: 'Time to send it into the world.' },
  scheduling:    { title: 'Bookable.',                 description: 'Clients can now grab time on their own.' },
  payments:      { title: "Money's in.",               description: 'Stripe is connected — invoices route to your account.' },
  studio_site:   { title: 'Site is live.',             description: 'Your Business Site just went public.' },
  first_inquiry: { title: 'Someone wants to work with you.', description: 'First inquiry just landed.' },
  first_client:  { title: 'First client on the books.', description: 'A relationship Orbit will help you carry.' },
  first_project: { title: 'First project underway.',   description: 'The work begins.' },
};

function readSeen(): Set<string> {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function writeSeen(set: Set<string>) {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(Array.from(set)));
  } catch {
    /* ignore quota/private-mode errors */
  }
}

export function useActivationCelebrations(steps: ActivationStep[]) {
  const { toast } = useToast();
  const seededRef = useRef(false);

  useEffect(() => {
    if (!steps.length) return;
    const seen = readSeen();

    // First-ever run: seed currently-completed steps so we don\u2019t
    // celebrate retroactively for studios already past these milestones.
    if (!seededRef.current && !localStorage.getItem(SEED_FLAG)) {
      for (const s of steps) if (s.completed) seen.add(s.id);
      writeSeen(seen);
      try {
        localStorage.setItem(SEED_FLAG, '1');
      } catch {
        /* ignore */
      }
      seededRef.current = true;
      return;
    }
    seededRef.current = true;

    let changed = false;
    for (const step of steps) {
      if (step.completed && !seen.has(step.id)) {
        seen.add(step.id);
        changed = true;
        const copy = COPY[step.id];
        if (copy) {
          toast({ title: copy.title, description: copy.description });
        }
      }
    }
    if (changed) writeSeen(seen);
  }, [steps, toast]);
}

export default useActivationCelebrations;